import { useLayoutEffect, useMemo, useState } from 'react'

import { HighlightsWidgetAttributeItem } from 'features/views/LayoutEditor/widgets/HighlightsWidget/attributes/types'

type UseHighlightsWidgetItemListStateProps = {
    items: HighlightsWidgetAttributeItem[]
}

export function useHighlightsWidgetItemListState({ items }: UseHighlightsWidgetItemListStateProps) {
    const [columnCount, setColumnCount] = useState(0)
    const [wrapperRef, setWrapperRef] = useState<HTMLDivElement | null>(null)
    useLayoutEffect(() => {
        const wrapper = wrapperRef
        if (!wrapper) return

        const calculateColumnCount = () => {
            const computedStyle = window.getComputedStyle(wrapper)

            const columnCount = computedStyle.gridTemplateColumns.split(' ').length

            setColumnCount(columnCount)
        }

        calculateColumnCount()

        const resizeObserver = new ResizeObserver(calculateColumnCount)
        resizeObserver.observe(wrapper)

        return () => {
            resizeObserver.disconnect()
        }
    }, [wrapperRef, items.length])

    const itemsByRow: HighlightsWidgetAttributeItem[][] = useMemo(() => {
        if (!columnCount) return [items]

        const rowCount = Math.ceil(items.length / columnCount)

        const itemsByRow: HighlightsWidgetAttributeItem[][] = []
        for (let i = 0; i < rowCount; i++) {
            itemsByRow.push(items.slice(i * columnCount, (i + 1) * columnCount))
        }

        return itemsByRow
    }, [columnCount, items])

    return useMemo(
        () => ({
            columnCount,
            ref: setWrapperRef,
            itemsByRow,
        }),
        [columnCount, itemsByRow]
    )
}
